import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import useOrderTracking from '../../../../hooks/useOrderTracking';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';

function useEddTimeRange() {
  const { state } = usePageContext();
  const { localeLanguage, localeCountry } = state;

  const { getCurrentShipment } = useOrderTracking();
  const { eddTimeRange } = getCurrentShipment() || {};

  const formattedEddTimeRange = useMemo(() => {
    if (!eddTimeRange || !eddTimeRange[0] || !eddTimeRange[1]) return null;

    const formatTimeWithTimezone = (dateString: string): string | null => {
      if (typeof dateString !== 'string') return null;

      const date = parseISO(dateString);
      const localeString = `${localeLanguage}${localeCountry ? `-${localeCountry}` : ''}`;

      // will match -07, -0700, -07:00, Z
      const timezoneMatch = dateString.match(/([+-]\d{2}:?\d{0,2}|Z)$/);

      // use browser timezone if no timezone in date string
      if (!timezoneMatch) {
        return date.toLocaleTimeString(localeString, {
          hour: 'numeric',
          minute: '2-digit',
        });
      }

      let timezone = timezoneMatch[1];

      // normalize timezone format for date-fns-tz
      if (timezone === 'Z') {
        timezone = '+00:00';
      } else if (!timezone.includes(':')) {
        // handle -10 -> -10:00
        timezone =
          timezone.length === 3
            ? `${timezone}:00`
            : `${timezone.slice(0, 3)}:${timezone.slice(3)}`;
      }

      const hourStr = formatInTimeZone(date, timezone, 'HH');
      const minuteStr = formatInTimeZone(date, timezone, 'mm');

      // just formats the time, but full date is necessary for toLocaleTimeString
      const localDate = new Date(
        2000,
        0,
        1,
        parseInt(hourStr, 10),
        parseInt(minuteStr, 10),
      );

      // formatInTimeZone only accepts a date-fns-tz locale type, so we have to do this if we want to use dynamic locales
      return localDate.toLocaleTimeString(localeString, {
        hour: 'numeric',
        minute: '2-digit',
      });
    };

    const formattedEddTimeBegin = formatTimeWithTimezone(eddTimeRange[0]);
    const formattedEddTimeEnd = formatTimeWithTimezone(eddTimeRange[1]);

    return formattedEddTimeBegin && formattedEddTimeEnd
      ? [formattedEddTimeBegin, formattedEddTimeEnd]
      : null;
  }, [eddTimeRange, localeLanguage, localeCountry]);

  return formattedEddTimeRange;
}

export default useEddTimeRange;
